/**
 * Headwear for modular figures: crowns, pins, ribbons, bands and hats, drawn as pixel grids and
 * attached to an anchor — the hairstyle's crown or buns, or the head itself — so one item fits
 * every hairstyle. Each item sets its depth for faces toward and away from the camera (a ribbon
 * trails behind the body, but over the back when the figure walks away) and can hide hair parts
 * it covers.
 *
 * Grid legend is per item (`colours`); `ox`/`oy` is the grid cell on the anchor.
 */
import type { View } from '../sprites';
import type { Grid } from './hair';
import type { LeftArt } from './leftArt';

export interface Headwear {
  id: string;
  label: string;
  /** `crown`: the hairstyle's crown; `buns`: one on every bun (the crown if none); `head`: the head joint. */
  anchor: 'crown' | 'buns' | 'head';
  colours: Readonly<Record<string, string>>;
  /** Depth relative to the head (`front`) or absolute behind the body when negative. */
  z: { front: number; back: number };
  /** Loose ends below the anchor sway with the figure's movement (ribbon tails). */
  flutters?: boolean;
  /** Hair parts hidden while worn. */
  hides?: readonly ('bun' | 'tail')[];
  /** A grid per view; a view left out shows nothing (a mask seen from behind). */
  grids: Partial<Record<View, Grid>>;
  /** Worn on one side: how it is drawn on the left-facing views (rig/leftArt.ts). */
  left?: LeftArt;
}

export const HEADWEAR: readonly Headwear[] = [
  {
    id: 'guan',
    label: 'มงกุฎกวน',
    // A small gold crown clasped on the front of the bun, a pin through it.
    anchor: 'crown',
    colours: { g: '#e8c27e', G: '#a8834e', s: '#f2ead2' },
    z: { front: 0.04, back: 0.05 },
    grids: {
      front: {
        ox: 3,
        oy: 3,
        rows: ['.g...g.', '.ggggg.', 'sgGgGgs', '.GGGGG.'],
      },
      front3: {
        ox: 3,
        oy: 3,
        rows: ['.g...g.', '.ggggg.', 'sgGgGgs', '.GGGGG.'],
      },
      side: {
        ox: 2,
        oy: 3,
        rows: ['g.g..', 'gggg.', 'gGGgs', 'GGG..'],
      },
      // From behind the crown is on the far side of the knot: only its prongs over the top and the
      // pin's two ends either side of the knot show.
      back3: {
        ox: 3,
        oy: 3,
        rows: ['.g...g.', '.G...G.', 's.....s'],
      },
      back: {
        ox: 3,
        oy: 3,
        rows: ['.g...g.', '.G...G.', 's.....s'],
      },
    },
  },
  {
    id: 'pin',
    label: 'ปิ่นปักผม',
    // A jade-headed pin pushed through each bun at a slant: its head and the first of the shaft
    // stand out on one side, its tip out the other, the middle hidden in the bun. The shaft is lit
    // along its top (s) and shaded under (S); the jade head has a glint (h) and a shadowed side (O),
    // a red bead hanging from it. Facing away, the head is on the other side of the bun.
    anchor: 'buns',
    colours: {
      s: '#e6d8b0',
      S: '#9a8a62',
      o: '#6cc8a2',
      O: '#2f7f66',
      h: '#c8f0dc',
      r: '#c63c4a',
      R: '#7e2230',
    },
    z: { front: 0.04, back: 0.04 },
    // Its head on one side: three-quarter on, the mirror image's pin (rig/leftArt.ts); side-on it looks the same.
    left: { flip: ['front3', 'back3'] },
    grids: {
      front: {
        ox: 4,
        oy: 3,
        rows: ['.......ho', '......oOO', '.....sSr.', '....S..R.', '.........', '.s.......', 'sS.......'],
      },
      front3: {
        ox: 4,
        oy: 3,
        rows: ['.......ho', '......oOO', '.....sSr.', '....S..R.', '.........', '.s.......', 'sS.......'],
      },
      side: {
        ox: 3,
        oy: 3,
        rows: ['......h', '.....oO', '....sSr', '...S..R', '.......', 's......', 'S......'],
      },
      back3: {
        ox: 4,
        oy: 3,
        rows: ['oh.......', 'OOo......', '.rSs.....', '.R..S....', '.........', '.......s.', '.......Ss'],
      },
      back: {
        ox: 4,
        oy: 3,
        rows: ['oh.......', 'OOo......', '.rSs.....', '.R..S....', '.........', '.......s.', '.......Ss'],
      },
    },
  },
  {
    id: 'ribbon',
    label: 'ริบบิ้นผูกผม',
    // A red ribbon knotted at the crown, two long tails trailing behind. From behind, a bow whose
    // loops are shaded underneath (d) round a dark knot (k), and two flat tails, lit on the edge
    // toward the light (l), that turn over at a fold (d) and show their shaded back below it.
    anchor: 'crown',
    colours: { r: '#c63c4a', R: '#8e2433', l: '#e07070', d: '#5e1624', k: '#6e1a28' },
    z: { front: -0.9, back: 0.05 },
    flutters: true,
    grids: {
      front: {
        ox: 4,
        oy: 1,
        rows: [
          '.rr...rr.',
          'rr.....rr',
          '.R.R.r.R.',
          '...r.r...',
          '...r.r...',
          '...r.R...',
          '...R.r...',
          '...r.r...',
          '...r.r...',
          '...r.R...',
          '...R.r...',
          '...r.r...',
          '...r.r...',
          '...r.R...',
          '...R.r...',
          '...r.r...',
          '...r.r...',
          '...r.R...',
          '...R.r...',
          '...r.r...',
        ],
      },
      front3: {
        ox: 13,
        oy: 1,
        rows: [
          '.............R.',
          '............rrr',
          '...........rrr.',
          '...........rr..',
          '.........rrr...',
          '........rrR....',
          '......Rrrrr....',
          '.....rrr.......',
          '....rR.........',
          '....Rr.........',
          '..rrr..........',
          '..rrr..........',
          '..Rr...........',
          '..rr...........',
          'rRr............',
          'rr.............',
        ],
      },
      side: {
        ox: 13,
        oy: 1,
        rows: [
          '.............R.',
          '............rrr',
          '...........rrr.',
          '...........rr..',
          '.........rrr...',
          '........rrR....',
          '......Rrrrr....',
          '.....rrr.......',
          '....rR.........',
          '....Rr.........',
          '..rrr..........',
          '..rrr..........',
          '..Rr...........',
          '..rr...........',
          'rRr............',
          'rr.............',
        ],
      },
      back3: {
        ox: 4,
        oy: 1,
        rows: [
          '.rl...rl.',
          'rRrl.rRrl',
          '.dRdkdRd.',
          '...rkl...',
          '..rl.Rl..',
          '..rl.rl..',
          '..Rl.rl..',
          '..dd.Rl..',
          '..RR.dd..',
          '..Rr.RR..',
          '.Rr..Rr..',
          '.Rr...Rr.',
          '.dd...Rr.',
          '.rl...dd.',
          '.rl...rl.',
          '.rl...rl.',
          '.dl....rl',
          '..d....dl',
        ],
      },
      back: {
        ox: 4,
        oy: 1,
        rows: [
          '.rl...rl.',
          'rRrl.rRrl',
          '.dRdkdRd.',
          '...rkl...',
          '..rl.Rl..',
          '..rl.rl..',
          '..Rl.rl..',
          '..dd.Rl..',
          '..RR.dd..',
          '..Rr.RR..',
          '.Rr..Rr..',
          '.Rr...Rr.',
          '.dd...Rr.',
          '.rl...dd.',
          '.rl...rl.',
          '.rl...rl.',
          '.dl....rl',
          '..d....dl',
        ],
      },
    },
  },
  {
    id: 'band',
    label: 'ผ้าคาดหน้าผาก',
    // A cloth band round the brow, knotted at the back.
    anchor: 'head',
    colours: { b: '#2f4a8a', B: '#1f3266' },
    z: { front: 0.04, back: 0.04 },
    grids: {
      front: {
        ox: 9,
        oy: 5,
        rows: ['.bbbbbbbbbbbbbbbb.', 'BBBBBBBBBBBBBBBBBB'],
      },
      front3: {
        ox: 13,
        oy: 5,
        rows: [
          '.....bbbbbbbbbbbbbbbb.',
          '...bBBBBBBBBBBBBBBBBBB',
          '...bB.................',
          '..bB..................',
          '..bB..................',
          '.bB...................',
          '.b....................',
          'b.....................',
        ],
      },
      side: {
        ox: 13,
        oy: 5,
        rows: [
          '....bbbbbbbbbbbbbbbbbb',
          '...bBBBBBBBBBBBBBBBBBB',
          '...bB.................',
          '..bB..................',
          '..bB..................',
          '.bB...................',
          '.b....................',
          'b.....................',
        ],
      },
      back3: {
        ox: 9,
        oy: 5,
        rows: [
          '.bbbbbBBBbbbbbbbb.',
          'BBBBBBbBbBBBBBBBBB',
          '......b.b.........',
          '......b.b.........',
          '.....b...b........',
          '.....b...b........',
          '.....b...b........',
          '....b.....b.......',
        ],
      },
      back: {
        ox: 9,
        oy: 5,
        rows: [
          '.bbbbbbBBBbbbbbbb.',
          'BBBBBBBbBbBBBBBBBB',
          '.......b.b........',
          '.......b.b........',
          '......b...b.......',
          '......b...b.......',
          '......b...b.......',
          '.....b.....b......',
        ],
      },
    },
  },
  {
    id: 'douli',
    label: 'หมวกงอบ',
    // A wide conical bamboo hat; the bun is hidden under it.
    anchor: 'head',
    colours: { k: '#c4a468', K: '#8a6e3e' },
    z: { front: 0.06, back: 0.06 },
    hides: ['bun'],
    grids: {
      front: {
        ox: 14,
        oy: 13,
        rows: [
          '.............Kk.............',
          '............Kkkk............',
          '...........kKkkkk...........',
          '..........KKKkKkkK..........',
          '.........kKkKKkkKkk.........',
          '.......kKkKKKkKkkKkkK.......',
          '.....kKkKKKkKkkKkkKkkKk.....',
          '..KKKkKkKKKkKkkKkkKkkKkkKk..',
          'kkkkkkkkkkkkkkkkkkkkkkkkkkkk',
          '.KKKKKKKKKKKKKKKKKKKKKKKKKK.',
        ],
      },
      front3: {
        ox: 14,
        oy: 13,
        rows: [
          '.............Kk.............',
          '............Kkkk............',
          '...........kKkkkk...........',
          '..........KKKkKkkK..........',
          '.........kKkKKkkKkk.........',
          '.......kKkKKKkKkkKkkK.......',
          '.....kKkKKKkKkkKkkKkkKk.....',
          '..KKKkKkKKKkKkkKkkKkkKkkKk..',
          'kkkkkkkkkkkkkkkkkkkkkkkkkkkk',
          '.KKKKKKKKKKKKKKKKKKKKKKKKKK.',
        ],
      },
      side: {
        ox: 14,
        oy: 13,
        rows: [
          '.............Kk.............',
          '............Kkkk............',
          '...........kKkkkk...........',
          '..........KKKkKkkK..........',
          '.........kKkKKkkKkk.........',
          '.......kKkKKKkKkkKkkK.......',
          '.....kKkKKKkKkkKkkKkkKk.....',
          '..KKKkKkKKKkKkkKkkKkkKkkKk..',
          'kkkkkkkkkkkkkkkkkkkkkkkkkkkk',
          '.KKKKKKKKKKKKKKKKKKKKKKKKKK.',
        ],
      },
      back3: {
        ox: 14,
        oy: 13,
        rows: [
          '.............Kk.............',
          '............Kkkk............',
          '...........kKkkkk...........',
          '..........KKKkKkkK..........',
          '.........kKkKKkkKkk.........',
          '.......kKkKKKkKkkKkkK.......',
          '.....kKkKKKkKkkKkkKkkKk.....',
          '..KKKkKkKKKkKkkKkkKkkKkkKk..',
          'kkkkkkkkkkkkkkkkkkkkkkkkkkkk',
          '.KKKKKKKKKKKKKKKKKKKKKKKKKK.',
        ],
      },
      back: {
        ox: 14,
        oy: 13,
        rows: [
          '.............Kk.............',
          '............Kkkk............',
          '...........kKkkkk...........',
          '..........KKKkKkkK..........',
          '.........kKkKKkkKkk.........',
          '.......kKkKKKkKkkKkkK.......',
          '.....kKkKKKkKkkKkkKkkKk.....',
          '..KKKkKkKKKkKkkKkkKkkKkkKk..',
          'kkkkkkkkkkkkkkkkkkkkkkkkkkkk',
          '.KKKKKKKKKKKKKKKKKKKKKKKKKK.',
        ],
      },
    },
  },
  {
    id: 'mask',
    label: 'ผ้าปิดหน้า',
    // A cloth mask over the nose and mouth, tied behind the ears; unseen from behind.
    anchor: 'head',
    colours: { m: '#2a2438', M: '#1a1828' },
    z: { front: 0.03, back: 0.03 },
    grids: {
      front: {
        ox: 7,
        oy: -4,
        rows: [
          'mmmmmmmmmmmmmm',
          'mmmmmmmmmmmmmm',
          '.mmmmmmmmmmmm.',
          '.mmmmmmmmmmmm.',
          '..mmmmmmmmmm..',
          '....MMMMMM....',
        ],
      },
      front3: {
        ox: 4,
        oy: -4,
        rows: [
          'mmmmmmmmmmmM',
          'mmmmmmmmmmmM',
          '.mmmmmmmmmmm',
          '..mmmmmmmmm.',
          '...mmmmmmm..',
          '.....MMMM...',
        ],
      },
      side: {
        ox: -2,
        oy: -4,
        rows: ['mmmmmmM', 'mmmmmmM', '.mmmmmm', '.mmmmm.', '..mmmm.', '...mm..'],
      },
    },
  },
  {
    id: 'veil',
    label: 'ผ้าคลุมหน้า (คู่หมวกงอบ)',
    // Sheer gauze hanging all round from a douli's brim; every third thread, so the face shows through.
    anchor: 'head',
    colours: { v: '#c8c2b6' },
    z: { front: 0.065, back: 0.065 },
    grids: {
      front: {
        ox: 14,
        oy: 3,
        rows: [
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
        ],
      },
      front3: {
        ox: 14,
        oy: 3,
        rows: [
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
        ],
      },
      side: {
        ox: 14,
        oy: 3,
        rows: [
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
        ],
      },
      back3: {
        ox: 14,
        oy: 3,
        rows: [
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
        ],
      },
      back: {
        ox: 14,
        oy: 3,
        rows: [
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
          'v..v..v..v..v..v..v..v..v..v.',
        ],
      },
    },
  },
  {
    id: 'foxEars',
    label: 'หูจิ้งจอก',
    // A fox spirit's ears standing up through the hair, pale inside where they face the camera.
    anchor: 'head',
    colours: { e: '#e4e8f2', i: '#f2c8d0' },
    z: { front: 0.04, back: 0.04 },
    grids: {
      front: {
        ox: 7,
        oy: 13,
        rows: ['.e..........e.', 'eie........eie', 'eie........eie', 'eee........eee'],
      },
      front3: {
        ox: 5,
        oy: 13,
        rows: ['.e......e.', 'eee....eie', 'eee....eie', 'eee....eee'],
      },
      side: {
        ox: 1,
        oy: 13,
        rows: ['.e.', 'eie', 'eie', 'eee'],
      },
      back3: {
        ox: 6,
        oy: 13,
        rows: ['.e........e.', 'eee......eee', 'eee......eee', 'eee......eee'],
      },
      back: {
        ox: 7,
        oy: 13,
        rows: ['.e..........e.', 'eee........eee', 'eee........eee', 'eee........eee'],
      },
    },
  },
  {
    id: 'futou',
    label: 'หมวกขุนนาง',
    // A black scholar-official's cap with stiff wings out to each side.
    anchor: 'head',
    colours: { k: '#1e1c24', K: '#34303e' },
    z: { front: 0.06, back: 0.06 },
    hides: ['bun'],
    grids: {
      front: {
        ox: 18,
        oy: 15,
        rows: [
          '...............KKKKKK...............',
          '..............KKKKKKKK..............',
          '..............Kkkkkkkk..............',
          '............Kkkkkkkkkkkk............',
          '..........Kkkkkkkkkkkkkkkk..........',
          'K........Kkkkkkkkkkkkkkkkkk........K',
          'kkkkkkkk.Kkkkkkkkkkkkkkkkkk.kkkkkkkk',
          'K.......Kkkkkkkkkkkkkkkkkkkk.......K',
          '........Kkkkkkkkkkkkkkkkkkkk........',
          '........Kkkkkkkkkkkkkkkkkkkk........',
          '........Kkkkkkkkkkkkkkkkkkkk........',
        ],
      },
      front3: {
        ox: 14,
        oy: 15,
        rows: [
          '...........KKKKKK.............',
          '..........KKKKKKKK............',
          '..........Kkkkkkkk............',
          '........Kkkkkkkkkkkk..........',
          '......Kkkkkkkkkkkkkkkk........',
          '.....Kkkkkkkkkkkkkkkkkk.......',
          'kkkk.Kkkkkkkkkkkkkkkkkk.kkkkkk',
          '....Kkkkkkkkkkkkkkkkkkkk......',
          '....Kkkkkkkkkkkkkkkkkkkk......',
          '....Kkkkkkkkkkkkkkkkkkkk......',
          '....Kkkkkkkkkkkkkkkkkkkk......',
        ],
      },
      side: {
        ox: 13,
        oy: 15,
        rows: [
          '..........KKKKKK.......',
          '.........KKKKKKKK......',
          '.........Kkkkkkkk......',
          '.......Kkkkkkkkkkkk....',
          '.....Kkkkkkkkkkkkkkkk..',
          'K...Kkkkkkkkkkkkkkkkkk.',
          'kkk.Kkkkkkkkkkkkkkkkkk.',
          '...Kkkkkkkkkkkkkkkkkkkk',
          '...Kkkkkkkkkkkkkkkkkkkk',
          '...Kkkkkkkkkkkkkkkkkkkk',
          '...Kkkkkkkkkkkkkkkkkkkk',
        ],
      },
      back3: {
        ox: 16,
        oy: 15,
        rows: [
          '.............KKKKKK...........',
          '............KKKKKKKK..........',
          '............Kkkkkkkk..........',
          '..........Kkkkkkkkkkkk........',
          '........Kkkkkkkkkkkkkkkk......',
          '.......Kkkkkkkkkkkkkkkkkk.....',
          'kkkkkk.Kkkkkkkkkkkkkkkkkk.kkkk',
          '......Kkkkkkkkkkkkkkkkkkkk....',
          '......Kkkkkkkkkkkkkkkkkkkk....',
          '......Kkkkkkkkkkkkkkkkkkkk....',
          '......Kkkkkkkkkkkkkkkkkkkk....',
        ],
      },
      back: {
        ox: 18,
        oy: 15,
        rows: [
          '...............KKKKKK...............',
          '..............KKKKKKKK..............',
          '..............Kkkkkkkk..............',
          '............Kkkkkkkkkkkk............',
          '..........Kkkkkkkkkkkkkkkk..........',
          'K........Kkkkkkkkkkkkkkkkkk........K',
          'kkkkkkkk.Kkkkkkkkkkkkkkkkkk.kkkkkkkk',
          'K.......Kkkkkkkkkkkkkkkkkkkk.......K',
          '........Kkkkkkkkkkkkkkkkkkkk........',
          '........Kkkkkkkkkkkkkkkkkkkk........',
          '........Kkkkkkkkkkkkkkkkkkkk........',
        ],
      },
    },
  },
  {
    id: 'hood',
    label: 'ผ้าคลุมศีรษะ',
    // A cloth hood over the head and down past the jaw, open round the face.
    anchor: 'head',
    colours: { d: '#4a4e5e', D: '#33363f' },
    z: { front: 0.05, back: 0.05 },
    hides: ['bun', 'tail'],
    grids: {
      front: {
        ox: 11,
        oy: 13,
        rows: [
          '.......Dddddddd.......',
          '.....Dddddddddddd.....',
          '...Dddddddddddddddd...',
          '..Dddddddddddddddddd..',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
          'Dddd..............dddd',
        ],
      },
      front3: {
        ox: 11,
        oy: 13,
        rows: [
          '.......Dddddddd.......',
          '.....Dddddddddddd.....',
          '...Dddddddddddddddd...',
          '..Dddddddddddddddddd..',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
          'Ddddddd..............d',
        ],
      },
      side: {
        ox: 11,
        oy: 13,
        rows: [
          '.......Dddddddd......',
          '.....Dddddddddddd....',
          '...Dddddddddddddddd..',
          '..Dddddddddddddddddd.',
          '.Dddddddddddddddddddd',
          '.Dddddddddddddddddddd',
          '.Dddddddddddddddddddd',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
          'Dddddddddddddd.......',
        ],
      },
      back3: {
        ox: 11,
        oy: 13,
        rows: [
          '.......Dddddddd.......',
          '.....Dddddddddddd.....',
          '...Dddddddddddddddd...',
          '..Dddddddddddddddddd..',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
        ],
      },
      back: {
        ox: 11,
        oy: 13,
        rows: [
          '.......Dddddddd.......',
          '.....Dddddddddddd.....',
          '...Dddddddddddddddd...',
          '..Dddddddddddddddddd..',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          '.Dddddddddddddddddddd.',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
          'Dddddddddddddddddddddd',
        ],
      },
    },
  },
  {
    id: 'bandana',
    label: 'ผ้าโพกหัว',
    // A red cloth tied over the head, the knot's tails hanging at the back.
    anchor: 'head',
    colours: { r: '#b8343c', R: '#8a2430' },
    z: { front: 0.05, back: 0.05 },
    flutters: true,
    hides: ['bun'],
    grids: {
      front: {
        ox: 10,
        oy: 12,
        rows: [
          '.....rrrrrrrrrr.....',
          '...rrrrrrrrrrrrrr...',
          '..rrrrrrrrrrrrrrrr..',
          '.rrrrrrrrrrrrrrrrrr.',
          '.rrrrrrrrrrrrrrrrrr.',
          'rrrrrrrrrrrrrrrrrrrr',
          'rrrrrrrrrrrrrrrrrrrr',
          'RRRRRRRRRRRRRRRRRRRR',
        ],
      },
      front3: {
        ox: 12,
        oy: 12,
        rows: [
          '.......rrrrrrrrrr.....',
          '.....rrrrrrrrrrrrrr...',
          '....rrrrrrrrrrrrrrrr..',
          '...rrrrrrrrrrrrrrrrrr.',
          '...rrrrrrrrrrrrrrrrrr.',
          '..rrrrrrrrrrrrrrrrrrrr',
          '..rrrrrrrrrrrrrrrrrrrr',
          '.RRRRRRRRRRRRRRRRRRRRR',
          '.r....................',
          'r.....................',
          'R.....................',
        ],
      },
      side: {
        ox: 14,
        oy: 12,
        rows: [
          '.........rrrrrrrrrr.....',
          '.......rrrrrrrrrrrrrr...',
          '......rrrrrrrrrrrrrrrr..',
          '.....rrrrrrrrrrrrrrrrrr.',
          '.....rrrrrrrrrrrrrrrrrr.',
          '....rrrrrrrrrrrrrrrrrrrr',
          '..RRRrrrrrrrrrrrrrrrrrrr',
          '..rrRRRRRRRRRRRRRRRRRRRR',
          '..rR....................',
          '.rr.....................',
          '.rR.....................',
          '.rr.....................',
          'rR......................',
        ],
      },
      back3: {
        ox: 10,
        oy: 12,
        rows: [
          '.....rrrrrrrrrr.....',
          '...rrrrrrrrrrrrrr...',
          '..rrrrrrrrrrrrrrrr..',
          '.rrrrrrrrrrrrrrrrrr.',
          '.rrrrrrrrrrrrrrrrrr.',
          'rrrrrrrrrrrrrrrrrrrr',
          'rrrrrrRRRRrrrrrrrrrr',
          'RRRRRRrRRRRRRRRRRRRR',
          '......r..R..........',
          '......r..R..........',
          '.....r....R.........',
          '.....r....R.........',
          '.....r....R.........',
        ],
      },
      back: {
        ox: 10,
        oy: 12,
        rows: [
          '.....rrrrrrrrrr.....',
          '...rrrrrrrrrrrrrr...',
          '..rrrrrrrrrrrrrrrr..',
          '.rrrrrrrrrrrrrrrrrr.',
          '.rrrrrrrrrrrrrrrrrr.',
          'rrrrrrrrrrrrrrrrrrrr',
          'rrrrrrrrRRRRrrrrrrrr',
          'RRRRRRRRrRRRRRRRRRRR',
          '........r..R........',
          '........r..R........',
          '.......r....R.......',
          '.......r....R.......',
          '.......r....R.......',
        ],
      },
    },
  },
  {
    id: 'helmet',
    label: 'หมวกเกราะ',
    // A steel war helmet with a red plume and a guard over the nape.
    anchor: 'head',
    colours: { m: '#9aa2ae', n: '#6a7280', M: '#4e5462', r: '#c8323c', R: '#8e1e2a' },
    z: { front: 0.06, back: 0.06 },
    hides: ['bun', 'tail'],
    grids: {
      front: {
        ox: 11,
        oy: 19,
        rows: [
          '..........RR..........',
          '..........rr..........',
          '..........RR..........',
          '..........rr..........',
          '..........RR..........',
          '........nmmmmm........',
          '......nmmmmmmmmm......',
          '....nmmmmmmmmmmmmm....',
          '...nmmmmmmmmmmmmmmm...',
          '..nmmmmmmmmmmmmmmmmm..',
          '..nmmmmmmmmmmmmmmmmm..',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          'MMMMMMMMMMMMMMMMMMMMMM',
        ],
      },
      front3: {
        ox: 11,
        oy: 19,
        rows: [
          '........RR............',
          '........rr............',
          '.........RR...........',
          '.........rr...........',
          '..........RR..........',
          '........nmmmmm........',
          '......nmmmmmmmmm......',
          '....nmmmmmmmmmmmmm....',
          '...nmmmmmmmmmmmmmmm...',
          '..nmmmmmmmmmmmmmmmmm..',
          '..nmmmmmmmmmmmmmmmmm..',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          'MMMMMMMMMMMMMMMMMMMMMM',
        ],
      },
      side: {
        ox: 11,
        oy: 19,
        rows: [
          '......RR..............',
          '.......rr.............',
          '........RR............',
          '.........rr...........',
          '..........RR..........',
          '........nmmmmm........',
          '......nmmmmmmmmm......',
          '....nmmmmmmmmmmmmm....',
          '...nmmmmmmmmmmmmmmm...',
          '..nmmmmmmmmmmmmmmmmm..',
          '..nmmmmmmmmmmmmmmmmm..',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          'MMMMMMMMMMMMMMMMMMMMMM',
          'mmmmmmmm..............',
          'mmmmmmmm..............',
          'mmmmmmmm..............',
          'nnnnnnnn..............',
        ],
      },
      back3: {
        ox: 11,
        oy: 19,
        rows: [
          '............RR........',
          '...........rr.........',
          '...........RR.........',
          '..........rr..........',
          '..........RR..........',
          '........nmmmmm........',
          '......nmmmmmmmmm......',
          '....nmmmmmmmmmmmmm....',
          '...nmmmmmmmmmmmmmmm...',
          '..nmmmmmmmmmmmmmmmmm..',
          '..nmmmmmmmmmmmmmmmmm..',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          'MMMMMMMMMMMMMMMMMMMMMM',
          '.....mmmmmmmmmmmmmmmmm',
          '.....mmmmmmmmmmmmmmmmm',
          '.....mmmmmmmmmmmmmmmmm',
          '.....nnnnnnnnnnnnnnnnn',
        ],
      },
      back: {
        ox: 11,
        oy: 19,
        rows: [
          '..........RR..........',
          '..........rr..........',
          '..........RR..........',
          '..........rr..........',
          '..........RR..........',
          '........nmmmmm........',
          '......nmmmmmmmmm......',
          '....nmmmmmmmmmmmmm....',
          '...nmmmmmmmmmmmmmmm...',
          '..nmmmmmmmmmmmmmmmmm..',
          '..nmmmmmmmmmmmmmmmmm..',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          '.nmmmmmmmmmmmmmmmmmmm.',
          'MMMMMMMMMMMMMMMMMMMMMM',
          '.mmmmmmmmmmmmmmmmmmmm.',
          '.mmmmmmmmmmmmmmmmmmmm.',
          '.mmmmmmmmmmmmmmmmmmmm.',
          '.nnnnnnnnnnnnnnnnnnnn.',
        ],
      },
    },
  },
  {
    id: 'circlet',
    label: 'รัดเกล้าเงิน',
    // A thin silver band across the brow, a jade set over the forehead.
    anchor: 'head',
    colours: { G: '#c9ced8', j: '#5fbf9a', J: '#2f7f66' },
    z: { front: 0.045, back: 0.045 },
    grids: {
      front: { ox: 9, oy: 8, rows: ['........G.........', '........jj........', 'GGGGGGGGJJGGGGGGGG'] },
      front3: { ox: 8, oy: 8, rows: ['..........G......', '..........jj.....', 'GGGGGGGGGGJJGGGGG'] },
      side: { ox: 9, oy: 7, rows: ['................jj', 'GGGGGGGGGGGGGGGGJJ'] },
      back3: { ox: 8, oy: 6, rows: ['GGGGGGGGGGGGGGGGG'] },
      back: { ox: 9, oy: 6, rows: ['GGGGGGGGGGGGGGGGGG'] },
    },
  },
  {
    id: 'horns',
    label: 'เขาปีศาจ',
    // Dark horns curving up and out from the temples.
    anchor: 'head',
    colours: { h: '#3a2a2e', H: '#8a2a30' },
    z: { front: 0.05, back: 0.05 },
    grids: {
      front: {
        ox: 9,
        oy: 15,
        rows: [
          '.H..............H.',
          'H................H',
          'H................H',
          'hh..............hh',
          '.hh............hh.',
          '.hh............hh.',
          '..hh..........hh..',
        ],
      },
      front3: {
        ox: 5,
        oy: 15,
        rows: [
          '.H............H.',
          'H..............H',
          'H..............H',
          'hh............hh',
          '.hh..........hh.',
          '.hh..........hh.',
          '..hh........hh..',
        ],
      },
      side: { ox: 0, oy: 15, rows: ['.H..', 'H...', 'H...', 'hh..', '.hh.', '.hh.', '..hh'] },
      back3: {
        ox: 11,
        oy: 15,
        rows: [
          '.H............H.',
          'H..............H',
          'H..............H',
          'hh............hh',
          '.hh..........hh.',
          '.hh..........hh.',
          '..hh........hh..',
        ],
      },
      back: {
        ox: 9,
        oy: 15,
        rows: [
          '.H..............H.',
          'H................H',
          'H................H',
          'hh..............hh',
          '.hh............hh.',
          '.hh............hh.',
          '..hh..........hh..',
        ],
      },
    },
  },
  {
    id: 'flower',
    label: 'ดอกไม้ทัดหู',
    // A flower tucked behind the ear.
    anchor: 'head',
    colours: { p: '#f0a0b8', P: '#d0607e', y: '#f2d060', l: '#5a9a4a' },
    z: { front: 0.05, back: 0.05 },
    // Behind one ear: three-quarter on the mirror image's, side-on out of sight on the other ear (rig/leftArt.ts).
    left: { flip: ['front3', 'back3'], hide: ['side'] },
    grids: {
      front: { ox: 11, oy: 5, rows: ['pPp..', 'PyP..', 'pPp..', '...ll'] },
      front3: { ox: 8, oy: 5, rows: ['pPp..', 'PyP..', 'pPp..', '...ll'] },
      side: { ox: 4, oy: 5, rows: ['pPp..', 'PyP..', 'pPp..', '...ll'] },
      back3: { ox: -3, oy: 5, rows: ['..pPp', '..PyP', '..pPp', 'll...'] },
      back: { ox: -6, oy: 5, rows: ['..pPp', '..PyP', '..pPp', 'll...'] },
    },
  },
  {
    id: 'phoenix',
    label: 'มงกุฎหงส์',
    // A tall gold crown with phoenix wings and red beads, on the crown of the hair.
    anchor: 'crown',
    colours: { g: '#e8c27e', G: '#a8834e', r: '#c63c4a' },
    z: { front: 0.04, back: 0.01 },
    grids: {
      front: {
        ox: 5,
        oy: 5,
        rows: ['g....g....g', '.g..ggg..g.', '.gg.grg.gg.', 'ggggggggggg', 'gGrGgGgGrGg', '.GGGGGGGGG.'],
      },
      front3: {
        ox: 5,
        oy: 5,
        rows: ['g....g....g', '.g..ggg..g.', '.gg.grg.gg.', 'ggggggggggg', 'gGrGgGgGrGg', '.GGGGGGGGG.'],
      },
      side: { ox: 3, oy: 5, rows: ['..g..g', '.gg.gg', 'gg.ggg', 'gggggg', 'grGgGg', 'GGGGG.'] },
      back3: {
        ox: 5,
        oy: 5,
        rows: ['g....g....g', '.g..ggg..g.', '.gg.grg.gg.', 'ggggggggggg', 'gGrGgGgGrGg', '.GGGGGGGGG.'],
      },
      back: {
        ox: 5,
        oy: 5,
        rows: ['g....g....g', '.g..ggg..g.', '.gg.grg.gg.', 'ggggggggggg', 'gGrGgGgGrGg', '.GGGGGGGGG.'],
      },
    },
  },
  {
    id: 'feather',
    label: 'ปิ่นขนนก',
    // A long pheasant feather through each bun, sweeping up and back.
    anchor: 'buns',
    colours: { f: '#d89a4a', F: '#7a4a2a', g: '#e8c27e' },
    z: { front: 0.04, back: 0.04 },
    grids: {
      front: {
        ox: 0,
        oy: 9,
        rows: [
          '......f',
          '.....fF',
          '.....F.',
          '....fF.',
          '....F..',
          '...fF..',
          '..fF...',
          '.fF....',
          'gF.....',
          'g......',
        ],
      },
      front3: {
        ox: 6,
        oy: 9,
        rows: [
          'f......',
          'Ff.....',
          '.F.....',
          '.Ff....',
          '..F....',
          '..Ff...',
          '...Ff..',
          '....Ff.',
          '.....Fg',
          '......g',
        ],
      },
      side: {
        ox: 6,
        oy: 9,
        rows: [
          'f......',
          'Ff.....',
          '.F.....',
          '.Ff....',
          '..F....',
          '..Ff...',
          '...Ff..',
          '....Ff.',
          '.....Fg',
          '......g',
        ],
      },
      back3: {
        ox: 0,
        oy: 9,
        rows: [
          '......f',
          '.....fF',
          '.....F.',
          '....fF.',
          '....F..',
          '...fF..',
          '..fF...',
          '.fF....',
          'gF.....',
          'g......',
        ],
      },
      back: {
        ox: 0,
        oy: 9,
        rows: [
          '......f',
          '.....fF',
          '.....F.',
          '....fF.',
          '....F..',
          '...fF..',
          '..fF...',
          '.fF....',
          'gF.....',
          'g......',
        ],
      },
    },
  },
  {
    id: 'butterfly',
    label: 'ปิ่นผีเสื้อ',
    // A jewelled butterfly pin beside the crown.
    anchor: 'crown',
    colours: { b: '#8ab4f0', B: '#4a6ec0', g: '#e8c27e' },
    z: { front: 0.04, back: 0.02 },
    grids: {
      front: { ox: -2, oy: 4, rows: ['bb.bb', 'bBgBb', '.bgb.', '.b.b.'] },
      front3: { ox: -2, oy: 4, rows: ['bb.bb', 'bBgBb', '.bgb.', '.b.b.'] },
      side: { ox: 2, oy: 4, rows: ['bb.', 'bBg', '.bg', '.b.'] },
      back3: { ox: 6, oy: 4, rows: ['bb.bb', 'bBgBb', '.bgb.', '.b.b.'] },
      back: { ox: 6, oy: 4, rows: ['bb.bb', 'bBgBb', '.bgb.', '.b.b.'] },
    },
  },
];

export const HEADWEAR_BY_ID: Readonly<Record<string, Headwear>> = Object.fromEntries(
  HEADWEAR.map((h) => [h.id, h]),
);
